/** @jsxImportSource preact */
import { useEffect, useLayoutEffect, useRef } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { CloseIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import { Toast } from "./base-ui.mjs";
import { FORM_STYLES, formVars } from "./form-styles";

export interface NoticeToastProps {
  tokens: WidgetTokens;
  title?: string;
  /** 正文。 */
  children?: string;
  /** danger 用出错色：淡红底、危险色标题、不描边，用于出了问题的提示。 */
  tone?: "default" | "danger";
  /** 点关闭时调用；提示不会自己消失。 */
  onClose?: () => void;
}

type SeedProps = Omit<NoticeToastProps, "tokens">;

/** 挂载时放进一条提示；卸载时收掉。 */
function Seed({ title, children, tone, onClose }: SeedProps) {
  const manager = Toast.useToastManager();
  useEffect(() => {
    const id = manager.add({
      data: { tone },
      description: children,
      onClose,
      timeout: 0,
      title,
    });
    return () => {
      manager.close(id);
    };
    // 只在挂载时放进一条，内容变化时由外面换 key 重挂。
  }, []);
  return null;
}

function List() {
  const { toasts } = Toast.useToastManager();
  return toasts.map((toast) => (
    <Toast.Root
      className="gn-toast"
      data-tone={(toast.data as { tone?: string } | undefined)?.tone}
      key={toast.id}
      toast={toast}
    >
      <Toast.Content className="gn-toast-body">
        {toast.title && <Toast.Title className="gn-toast-title" />}
        <Toast.Description />
      </Toast.Content>
      <Toast.Close
        aria-label={m.widget_form_dismiss()}
        className="gn-toast-close"
      >
        <CloseIcon size={14} />
      </Toast.Close>
    </Toast.Root>
  ));
}

/**
 * 可关闭的浮层提示，浮在最近的定位祖先顶部，不占位。底层是 Base UI 的 Toast：
 * 读屏播报、按 F6 进入、关闭键、进出动画都由它处理。
 */
export function NoticeToast({ tokens: t, ...seed }: NoticeToastProps) {
  const viewport = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement>();
  useLayoutEffect(() => {
    const node = viewport.current;
    return () => {
      if (!node) return;
      const root = node.getRootNode();
      const focused =
        root instanceof ShadowRoot
          ? root.activeElement
          : node.ownerDocument.activeElement;
      // 更新提示会按事件 key 重挂；只在焦点还在旧提示里时送回原字段。
      if (
        focused &&
        node.contains(focused) &&
        previousFocus.current?.isConnected
      )
        previousFocus.current.focus({ preventScroll: true });
    };
  }, []);
  return (
    <Toast.Provider limit={1} timeout={0}>
      <style>{FORM_STYLES}</style>
      <Seed {...seed} />
      <Toast.Viewport
        ref={viewport}
        className="gn-toast-viewport"
        style={formVars(t)}
        onFocusCapture={(event) => {
          if (
            event.relatedTarget instanceof HTMLElement &&
            !event.currentTarget.contains(event.relatedTarget)
          )
            previousFocus.current = event.relatedTarget;
        }}
      >
        <List />
      </Toast.Viewport>
    </Toast.Provider>
  );
}
